import { CheckCircle2, Clock, Loader2, MessageSquare, Search, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { formatDate } from '../../lib/format';
import { usePublicApplicationStatus } from '../../api/applications';

/**
 * PUBLIC "check my application status" — /become-a-reseller/status?id=<reference>
 *
 * A purpose-built page rather than a reuse of the authenticated reseller ApplicationStatus:
 * that component is mounted inside the reseller shell, renders six mock-driven layouts, and
 * calls an ADMIN endpoint. On this route the visitor has no account at all, so it hung on a
 * 401 with no error state (an endless skeleton) and rendered off-centre without its shell.
 *
 * Here the reference number in the URL is the credential, the endpoint is public and narrow,
 * and every outcome — loading, not-found, and each status — is an explicit state.
 */

const STATUS_META: Record<
  string,
  { label: string; tone: 'success' | 'warning' | 'error' | 'info' | 'neutral'; blurb: string }
> = {
  pending: { label: 'Pending', tone: 'warning', blurb: 'Your application is in the queue. We usually respond within 1–2 business days.' },
  in_review: { label: 'In review', tone: 'info', blurb: 'An admin is reviewing your application right now.' },
  more_info_needed: { label: 'More info needed', tone: 'warning', blurb: 'We need a little more from you before we can decide — see the note below, and reply to the email we sent.' },
  approved: { label: 'Approved', tone: 'success', blurb: 'Welcome aboard. Check your email for the link to set your password and finish setting up your partner account.' },
  rejected: { label: 'Not approved', tone: 'error', blurb: 'We were not able to approve this application. The email we sent explains more.' },
  withdrawn: { label: 'Withdrawn', tone: 'neutral', blurb: 'This application was withdrawn.' },
};

function readIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const v = new URLSearchParams(window.location.search).get('id');
  return v && v.trim() ? v.trim() : null;
}

export function BecomeAResellerStatus() {
  // The previous page never read this at all, so the reference number it told applicants to
  // save was decorative.
  const [id, setId] = useState<string | null>(readIdFromUrl());
  const [draft, setDraft] = useState('');
  const { data, isLoading, isError, error } = usePublicApplicationStatus(id);

  const notFound = isError && (error as { status?: number } | null)?.status === 404;
  const meta = data ? STATUS_META[data.status] ?? STATUS_META.pending! : null;

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 px-4 py-10 sm:py-16">
      <div className="max-w-xl mx-auto">
        <div className="flex justify-center mb-8">
          <Logo />
        </div>

        {!id && (
          <Card className="p-6">
            <h1 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">Check your application status</h1>
            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300">
              Enter the reference number from your confirmation email.
            </p>
            <form
              className="mt-5 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (draft.trim()) setId(draft.trim());
              }}
            >
              <Input
                label="Reference number"
                placeholder="94b8721b-ad82-418c-8d05-cdbe83ce4912"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <Button type="submit" iconLeft={<Search className="w-4 h-4" />} disabled={!draft.trim()}>
                Check status
              </Button>
            </form>
          </Card>
        )}

        {id && isLoading && (
          <Card className="p-6">
            <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
              <Loader2 className="w-4 h-4 animate-spin" />
              Looking up your application…
            </div>
          </Card>
        )}

        {id && isError && (
          <Card className="p-6">
            <Banner tone={notFound ? 'warning' : 'error'} title={notFound ? 'No application found' : 'Something went wrong'}>
              {notFound
                ? 'We could not find an application with that reference number. Check it against your confirmation email — it is easy to miss a character.'
                : 'We could not load your application just now. Please try again in a moment.'}
            </Banner>
            <Button
              className="mt-4"
              variant="secondary"
              onClick={() => {
                setId(null);
                setDraft('');
              }}
            >
              Try another reference number
            </Button>
          </Card>
        )}

        {id && data && meta && (
          <Card className="p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400">
                  Application
                </div>
                <h1 className="mt-1 text-lg font-semibold text-zinc-950 dark:text-zinc-50">{data.orgName}</h1>
                <p className="text-sm text-zinc-600 dark:text-zinc-300">{data.applicantName} · {data.email}</p>
              </div>
              <Badge tone={meta.tone}>{meta.label}</Badge>
            </div>

            <p className="mt-4 text-sm text-zinc-700 dark:text-zinc-200">{meta.blurb}</p>

            <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-zinc-500 dark:text-zinc-400">Submitted</dt>
                <dd className="text-zinc-900 dark:text-zinc-100">{data.submittedAt ? formatDate(data.submittedAt) : '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-zinc-500 dark:text-zinc-400">Reviewed</dt>
                <dd className="text-zinc-900 dark:text-zinc-100">{data.reviewedAt ? formatDate(data.reviewedAt) : '—'}</dd>
              </div>
            </dl>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400 mb-2">
                Progress
              </div>
              {data.timeline.length === 0 ? (
                <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">No updates yet.</p>
              ) : (
                <ol className="space-y-3">
                  {data.timeline.map((e, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="mt-0.5 text-zinc-400 dark:text-zinc-500 flex-shrink-0">
                        {e.toStatus === 'approved' ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        ) : e.toStatus === 'rejected' ? (
                          <XCircle className="w-4 h-4 text-red-500" />
                        ) : e.kind === 'more_info_request' ? (
                          <MessageSquare className="w-4 h-4 text-amber-500" />
                        ) : (
                          <Clock className="w-4 h-4" />
                        )}
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm text-zinc-900 dark:text-zinc-100">
                          {e.text || (e.toStatus ? `Status changed to ${e.toStatus}.` : 'Application submitted.')}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">{e.at ? formatDate(e.at) : ''}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <p className="mt-6 text-xs text-zinc-500 dark:text-zinc-400">
              Reference <span className="mono">{data.id}</span>
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
